import { useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "../api/client";
import { NavLink, useRouter } from "../router";
import type { Permission } from "../api/types";
import { useAppState } from "../state/appState";
import { useSession } from "../state/session";
import { useToasts } from "../state/toasts";
import { surfaceHref, SURFACE_TITLES, type Surface } from "../surface";
import { TopBar } from "../ui/TopBar";
import { Modal } from "../ui/components";

/**
 * Рабочее место смены.
 *
 * Старший смены и стоит на кассе, и ведёт смену целиком: заказы, стоп-лист,
 * склад, возвраты, отчёты. Разносить это по двум адресам значило бы заставить
 * одного человека держать открытыми две панели и помнить, что где.
 *
 * Касса занимает всё полотно, остальные разделы убраны в меню шапки: у окна
 * важнее плитки товаров, а в отчёты заходят между гостями.
 */

interface NavItem {
  to: string;
  title: string;
  permission?: Permission;
  end?: boolean;
}

/**
 * Порядок разделов — это порядок работы, а не алфавит: сначала то, ради чего
 * сотрудник подошёл к планшету, в конце то, куда заглядывают раз в смену.
 */
const SECTIONS: NavItem[] = [
  { to: "/", title: "Касса", permission: "orders.counter", end: true },
  { to: "/orders", title: "Заказы", permission: "orders.view" },
  // Раздел меню на кассе — это стоп-лист: правку цен и состава сервер
  // старшему смены всё равно не разрешит, и кнопки для неё скрыты.
  { to: "/menu", title: "Стоп-лист", permission: "stoplist.manage" },
  { to: "/stock", title: "Склад", permission: "stock.view" },
  { to: "/purchases", title: "Закупки", permission: "stock.view" },
  { to: "/payments", title: "Платежи и возвраты", permission: "orders.view" },
  { to: "/analytics", title: "Аналитика", permission: "analytics.view" },
  // Долг по чекам останавливает приём заказов, и чинит его смена, а не владелец.
  { to: "/devices", title: "Устройства", permission: "devices.manage" }
];

/** Соседние поверхности: другие поддомены, поэтому обычные ссылки. */
const NEIGHBOURS: { surface: Exclude<Surface, "unknown">; permission: Permission }[] = [
  { surface: "kitchen", permission: "orders.status.kitchen" },
  { surface: "courier", permission: "orders.claim" },
  { surface: "admin", permission: "settings.manage" }
];

interface DevicesData {
  fiscalState: {
    unsent: number;
    oldestAt: string | null;
    shiftOpen: boolean;
    paper: boolean;
    at: string | null;
  } | null;
}

export function CounterLayout({ children }: { children: ReactNode }) {
  const { can } = useSession();
  const { path } = useRouter();
  const { snapshot } = useAppState();

  const items = SECTIONS.filter(item => !item.permission || can(item.permission));
  const neighbours = NEIGHBOURS.filter(item => can(item.permission));
  const current = items.find(item => (item.end ? path === item.to : path.startsWith(item.to)));

  return (
    <div className="shell">
      {/* Название совпадает с тем, как этот экран назван в админке и в адресе:
          одно место — одно имя, иначе смена ищет «кассу» и не находит. */}
      <TopBar
        title="Касса"
        subtitle={current && !current.end ? current.title.toLowerCase() : "рабочее место смены"}
        extra={
          <>
            {can("devices.manage") ? <FiscalShiftControl disabled={snapshot === null} /> : null}
            <SectionMenu items={items} neighbours={neighbours} current={current} />
          </>
        }
      />

      {children}
    </div>
  );
}

function FiscalShiftControl({ disabled }: { disabled: boolean }) {
  const { snapshot } = useAppState();
  const { notify } = useToasts();
  const [devices, setDevices] = useState<DevicesData | null>(null);
  const [confirming, setConfirming] = useState<"open" | "close" | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const result = await api.get<DevicesData>("/api/admin/devices");
        if (!cancelled) setDevices(result);
      } catch {
        if (!cancelled) setDevices(null);
      }
    }

    void load();
    const timer = window.setInterval(() => void load(), 30_000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [snapshot?.deviceJobs.length]);

  async function submit(action: "open" | "close") {
    setBusy(true);
    try {
      await api.once.post(`/api/admin/devices/fiscal-shift/${action}`);
      notify(action === "open" ? "Команда открытия смены отправлена на ФР" : "Команда закрытия смены отправлена на ФР", "ok");
      setDevices(await api.get<DevicesData>("/api/admin/devices"));
      setConfirming(null);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось отправить команду на ФР", "error");
    } finally {
      setBusy(false);
    }
  }

  const shiftKnown = devices?.fiscalState !== null && devices?.fiscalState !== undefined;
  const shiftOpen = devices?.fiscalState?.shiftOpen === true;
  const actionText = confirming === "open" ? "Открыть смену" : "Закрыть смену";

  return (
    <>
      <div className="shift-head">
        <span className={`shift-state ${shiftOpen ? "ok" : "warn"}`}>
          ФР: {shiftKnown ? (shiftOpen ? "смена открыта" : "смена закрыта") : "нет статуса"}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          disabled={disabled || busy}
          onClick={() => setConfirming("open")}
        >
          Открыть смену
        </button>
        <button
          type="button"
          className="btn btn-sm btn-danger"
          disabled={disabled || busy}
          onClick={() => setConfirming("close")}
        >
          Закрыть смену
        </button>
      </div>

      {confirming ? (
        <Modal
          title={actionText}
          onClose={() => setConfirming(null)}
          actions={
            <>
              <button type="button" className="btn" disabled={busy} onClick={() => setConfirming(null)}>
                Отмена
              </button>
              <button
                type="button"
                className={`btn ${confirming === "open" ? "btn-primary" : "btn-danger"}`}
                disabled={busy}
                onClick={() => void submit(confirming)}
              >
                {actionText}
              </button>
            </>
          }
        >
          {confirming === "open" ? (
            <div className="confirm-copy">
              <p>
                Команда откроет фискальную смену на онлайн-кассе. После этого заказы можно
                пробивать в текущую смену, а ФР будет печатать чеки и отправлять данные в ОФД.
              </p>
              <p className="muted">
                Обычно это делают в начале рабочего дня. Если смена уже открыта, ФР просто
                вернёт ответ, что она уже открыта.
              </p>
            </div>
          ) : (
            <div className="confirm-copy">
              <p>
                Команда закроет фискальную смену и напечатает Z-отчёт. После закрытия новые
                фискальные чеки не пробиваются, пока смену снова не откроют.
              </p>
              <p className="muted">
                Обычно это делают в конце рабочего дня, когда все оплаты и возвраты уже завершены.
              </p>
            </div>
          )}
        </Modal>
      ) : null}
    </>
  );
}

function SectionMenu({
  items,
  neighbours,
  current
}: {
  items: NavItem[];
  neighbours: { surface: Exclude<Surface, "unknown">; permission: Permission }[];
  current: NavItem | undefined;
}) {
  const [open, setOpen] = useState(false);
  const holder = useRef<HTMLDivElement>(null);

  /*
   * Меню закрывается по Escape и по нажатию мимо. Без этого на планшете оно
   * остаётся раскрытым и перекрывает корзину: касса под ним, а промахнуться
   * мимо узкого списка проще, чем попасть.
   */
  useEffect(() => {
    if (!open) return;

    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    function onPointer(event: PointerEvent) {
      if (!holder.current?.contains(event.target as Node)) setOpen(false);
    }

    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  if (items.length <= 1 && neighbours.length === 0) return null;

  return (
    <div className="sections" ref={holder}>
      <button
        type="button"
        className="btn btn-sm"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(value => !value)}
      >
        {current ? current.title : "Разделы"} ▾
      </button>

      {open ? (
        <nav className="sections-menu" aria-label="Разделы смены" onClick={() => setOpen(false)}>
          {items.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}>
              {item.title}
            </NavLink>
          ))}

          {neighbours.length > 0 ? (
            <>
              <p className="sections-group">Другие экраны</p>
              {neighbours.map(item => (
                <a key={item.surface} href={surfaceHref(item.surface)}>
                  {SURFACE_TITLES[item.surface]}
                </a>
              ))}
            </>
          ) : null}
        </nav>
      ) : null}
    </div>
  );
}
